'use client'; // Asegúrate de que esté al principio del archivo
import React from 'react';  // Importa React si no está importado
import Head from 'next/head';  
import TechTags from '../../utils/TechTags';  
const topics = [
    {
      id: 'sketches-in-technical-drawings',
      label: 'Sketches in Technical Drawings',
      content: [
        'Sketching is an essential step in the technical drawing process, serving as the foundation for more refined drafts and precise illustrations.',
        'It allows designers, engineers, and architects to quickly convey ideas before committing to detailed work with precise measurements.',
        'There are different types of sketches, including freehand sketches, technical sketches, and conceptual sketches, each serving a unique purpose in the design process.',
        'On my blog page Mastering Technical Drawings: Technical Drawing Fundamentals, find the second part of this series follow the link down below to read more.',
        
      ],
      link: 'https://dravvt.com/blog/mastering-technical-drawings-fundamentals',
      linkText: 'Mastering Technical Drawings: Technical Drawing Fundamentals',
      imgSrc: 'https://images.unsplash.com/photo-1600428610161-e98636332e98?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D', 
      imgAlt: 'A hand-drawn technical sketch of a mechanical part. image from <a href="https://unsplash.com/@kumpan_electric">Kumpan Electric</a> on <a href="https://unsplash.com/de/fotos/person-die-auf-whitepaper-schreibt-MeTEZ0Uk99c">Unsplash</a>'
      
    },
  
    {
      id: 'essential-drawing-tools',
      label: 'Essential Drawing Tools',
      content: [
        'Technical drawings require specialized tools to ensure precision and clarity in the design process.',
        'Common tools include rulers, compasses, protractors, T-squares, and French curves, which help create accurate straight lines, arcs, and curves.',
        'Modern digital tools such as CAD (Computer-Aided Design) software have revolutionized technical drawing, offering enhanced accuracy and efficiency compared to traditional methods.',
        '**CAD software** allows designers to create detailed, precise, and scalable technical drawings on a computer. Programs like AutoCAD, SolidWorks, and Rhino provide powerful tools for drafting, modeling, and simulating designs in 2D and 3D.',
        '**CAD** is incredibly useful because it simplifies many aspects of the design process, such as modifying, scaling, and sharing drawings. It eliminates the need for manual calculations and reduces human error, providing more accuracy and speed.',
        'Additionally, CAD software allows for easy collaboration, as designs can be shared digitally with teams and clients. It also enables the creation of complex simulations, 3D models, and virtual prototypes, making it an essential tool in industries like architecture, engineering, and product design.'
      ],
      imgSrc: 'https://images.unsplash.com/photo-1416339158484-9637228cc908?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      imgAlt: 'A collection of essential technical drawing tools on a drafting table. image from <a href="https://unsplash.com/de/@ugmonk?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Jeff Sheldon</a> on <a href="https://unsplash.com/de/fotos/grauer-stahlziehkompass-in-der-nahe-von-braunem-lederetui-8z2Q6XWLYa4?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Unsplash</a>',
      
    },
  
    {
      id: 'orthogonal-projection',
      label: 'Orthogonal Projection',
      content: [
        'Orthogonal projection is a method used in technical drawings to represent three-dimensional objects using multiple two-dimensional views.',
        'It typically includes front, top, and side views, each drawn to scale without perspective distortion.',
        'This method is widely used in engineering and architecture because it allows precise measurements and unambiguous representation of objects.',
        'There are several types of orthogonal projections, each providing a different perspective of the object. The most common types are **first-angle projection** and **third-angle projection**.',
        '**First-angle projection** is primarily used in Europe and Asia. In this method, the object is imagined to be placed in the first quadrant, with the views drawn around the object as if it were being viewed from the outside. This technique can sometimes be less intuitive for those accustomed to other methods.',
        '**Third-angle projection**, commonly used in North America, places the object in the third quadrant. The views are projected onto the drawing plane, giving a more straightforward way of visualizing the object, where the front view is placed on the drawings front, the top view above, and the side view to the side.',
        'Both methods allow the creation of multiple views of an object, ensuring that all necessary details are included, but the choice of method typically depends on the regional or industry-specific conventions.'
      ],
      imgSrc: 'https://upload.wikimedia.org/wikipedia/commons/4/41/Graphical_projection_comparison.png',
      imgAlt: 'Graphical projection comparison. Image from <a href="https://commons.wikimedia.org/w/index.php?curid=8497328" target="_blank">SharkD - Own work</a>Licensed under, <a href="https://creativecommons.org/licenses/by-sa/3.0/" target="_blank">CC BY-SA 3.0</a>.'

    },
  
    {
      id: 'axonometric-projection',
      label: 'Axonometric Projection',
      content: [
        'Axonometric projection is a technique used to illustrate three-dimensional objects in a way that maintains proportionality without perspective distortion.',
        'There are three main types: isometric, dimetric, and trimetric projections, each differing in the angles and scales applied to the axes.',
        'Isometric projection, the most common type, represents the three axes equally, making it useful for visualizing objects in engineering and architectural designs.',
        'Dimetric projection, on the other hand, distorts two axes equally while the third axis is at a different scale, making it less commonly used but sometimes useful for visualizing objects at certain angles.',
        'Trimetric projection differs from both isometric and dimetric in that all three axes are drawn at different scales and angles, making it the most distorted but also offering a more flexible and detailed view of complex objects.'
      ],
      imgSrc: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4c/Axonometric_projections.png/800px-Axonometric_projections.png',
      imgAlt: 'By Phasmatisnox (updated by Allefant) - Own work, CC BY 3.0, https://commons.wikimedia.org/w/index.php?curid=5988307'
      
    },
  
    {
      id: 'oblique-projection',
      label: 'Oblique Projection',
      content: [
        'Oblique projection is a drawing method where the front face of an object is drawn in true scale, while the depth is represented using an angle, usually 30 or 45 degrees.',
        'This technique allows designers to quickly convey an object’s form while keeping one face undistorted, making it easier to interpret.',
        'There are three main types of oblique projection: general oblique, cavalier, and cabinet projections, each with different approaches to scaling the depth axis.',
        'In **general oblique projection**, the depth is drawn at full scale, which can make the object appear too elongated or distorted.',
        'In **cavalier projection**, the depth axis is also drawn at full scale, but it is angled at 45 degrees. This creates a more dynamic representation but can also exaggerate the depth.',
        'In **cabinet projection**, the depth is scaled to half its actual size, making the object appear less distorted and more realistic, though it sacrifices some of the dynamic effect seen in cavalier projection.'
      ],
      imgSrc: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Comparison_of_graphical_projections.svg/800px-Comparison_of_graphical_projections.svg.png',
      imgAlt: 'By Cmglee - Own work, CC BY-SA 4.0, https://commons.wikimedia.org/w/index.php?curid=83384053'
      
    },
  
    {
      id: 'paper-sizes-and-standards',
      label: 'Paper Sizes and Standards',
      content: [
        'Technical drawings adhere to standardized paper sizes to ensure consistency in documentation and communication across industries.',
        'The ISO 216 standard defines the A-series paper sizes (e.g., A4, A3, A2, A1, A0), commonly used worldwide for technical and engineering drawings.',
        'In North America, the ANSI standard is followed, with letter, tabloid, and engineering sizes such as ANSI A, B, C, D, and E.',
        'The **ANSI** (American National Standards Institute) standard was developed in the United States as a response to the need for uniformity in paper sizes for various industries, including engineering and architecture. Unlike the ISO 216 system, the ANSI paper sizes are based on the "inch" system rather than the metric system, which is more commonly used worldwide.',
        'The ANSI standard includes sizes such as ANSI A (8.5 x 11 inches) for letter-sized paper, ANSI B (11 x 17 inches) for tabloid, and larger sizes like ANSI C (17 x 22 inches), ANSI D (22 x 34 inches), and ANSI E (34 x 44 inches), typically used for larger technical drawings and blueprints.'
      ],
      imgSrc: 'https://images.unsplash.com/photo-1619759247142-b457e9e6b634?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      imgAlt: 'Image of hand touching paper, experimenting with different materials. image from <a href="https://unsplash.com/de/@pickawood?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Pickawood</a> auf <a href="https://unsplash.com/de/fotos/person-die-auf-whitepaper-schreibt-ZRVSxFkbUss?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Unsplash</a>'

    }
  ];
  // Function to get tag names from tag IDs
const getTagNames = (tagIds) => {
  return tagIds.map(id => {
    const tag = TechTags.find(tag => tag.id === parseInt(id));  // Search for tag by id
    return tag ? tag.name : 'Unknown';  // Return the tag name, or 'Unknown' if not found
  });
};
// Definir un componente Section para reutilizar
const Section = ({ id, title, content = [],link, linkText, imgSrc, imgAlt }) => (
    <div id={id} className="mt-10">
      <h3 className="text-2xl font-bold dark:text-blue-100 text-neutral-950">{title}</h3>
      <div className="dark:text-gray-300 text-neutral-800 text-base mt-2">
        {content.map((para, index) => (
          <p key={index} className="mt-2">
            {para}
          </p>
        ))}
      </div>
       {/* Enlace condicional */}
    {link && linkText && (
      <p className="mt-4 text-sm text-blue-500">
        Read more: {' '}
        <a href={link} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-700">
          {linkText}
        </a>
      </p>
    )}
      <div className="grid min-h-[140px] w-full place-items-center overflow-x-scroll rounded-lg p-6 lg:overflow-visible">
        <img className="object-cover object-center w-full h-96" src={imgSrc} alt={imgAlt} />
        <p className="mt-2 text-sm text-gray-600">{imgAlt}</p>
      </div>
    </div>
  );
  const MasteringTechnicalDrawings = ({
    // Propiedades dentro del componente
    title = "Mastering Technical Drawings: A Complete Guide to Projection Types & Design Principles",
    description = "Learn about technical drawings, projection types (axonometric, orthogonal, oblique), CAD, and drawing tools. Explore 2D vs. 3D design, paper sizes, and industry standards for accurate technical documentation",//desde aca cambiar 
    category = ['Creators', 'CAD'],
    Techtags= ['30','31','32','33','34','35','36','37','38','39','40','41','42','43'],
    autor = "Dravvt",
    slug = "https://dravvt.com/blog/mastering-technical-drawings-a-complete-guide-to-projection-types-and-design-principles",
  }) => {

      // Use getTagNames function to fetch the tag names based on tagIds
  const tagNames = getTagNames(Techtags);
    // Objeto article dentro del componente
    const article = {
      imageUrl: 'https://images.unsplash.com/photo-1600869009498-8d429f88d4f5?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    };
 // Assuming Techtags is an array of objects or ids, you might want to map through it like this

  return (
    <>
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content={autor} />
        <meta name="keywords" content={tagNames.join(', ')} />
      {/* Meta para redes sociales */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={article.imageUrl} />
      <meta property="og:url" content= {slug}/>
        <meta property="og:type" content="article" />
      
      {/* Meta para Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={article.imageUrl} />
      <meta name="twitter:url" content={slug} />

    </Head>
    <div>
      <div className="dark:bg-gray-900 bg-transparent border border-neutral-950 text-neutral-950 p-4 rounded-lg shadow-md mb-4 max-w-xs mx-auto text-xs dark:text-gray-400 flex flex-col space-y-2">
        <h1 className="text-lg font-bold text-neutral-950 dark:text-white">{title || 'Default Title'}</h1>
        <p className="font-medium text-orange-600 dark:text-orange-400">Category: {category.join(', ')}</p>
        <p className="dark:text-gray-300 text-neutral-700">{description}</p>
        <p className="font-medium text-blue-500">Tags: {tagNames.slice(0, 3).join(', ')}</p>
      </div>
      <div className="article-content">
        <div className="max-w-screen-xl mx-auto p-5 sm:p-8 md:p-12 relative">
          <div
            className="bg-cover bg-center h-64 text-center overflow-hidden"
            style={{
              height: '450px',
              backgroundImage: `url(${article.imageUrl})`,
               
            }}
               aria-label='Foto von <a href="https://unsplash.com/de/@kumpan_electric?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Kumpan Electric</a> auf <a href="https://unsplash.com/de/fotos/schwarzer-flachbildschirm-SYo5eazBrls?utm_content=creditCopyText&utm_medium=referral&utm_source=unsplash">Unsplash</a>'
          ></div>
          <div className="max-w-2xl mx-auto">
            <div className="mt-3 bg-transparent rounded-b lg:rounded-b-none lg:rounded-r flex flex-col justify-between leading-normal">
              <a href="#" className="text-base text-blue-600 dark:text-gray-100 uppercase font-medium transition duration-500 ease-in-out">
                Arts
              </a>
              ,{' '}
              <a href="#" className="text-xs text-blue-600 dark:text-gray-100 uppercase font-medium transition duration-500 ease-in-out">
                Technology
              </a>
              <p></p>
              <p className="dark:text-gray-500 text-neutral-700 text-xs mt-2">Written By: Dravvt</p>
              <div className="dark:bg-gray-900 bg-transparent border border-neutral-950 p-4 rounded-lg shadow-md mb-4 max-w-md mx-auto text-base text-gray-400 flex flex-col space-y-2">
    <h3 className="text-lg font-semibold text-neutral-950 dark:text-blue-100 mb-3">
      Topics Covered:
    </h3>
    <ul className="list-disc pl-5">
      {topics.map((topic) => (
        <li key={topic.id}>
          <a
            href={`#${topic.id}`}
            className="dark:text-gray-200 text-neutral-700 hover:text-blue-500 dark:hover:text-gray-600"
          >
            {topic.label}
          </a>
        </li>
      ))}
    </ul>
  </div>



  {topics.map((topic) => (
  <Section
    key={topic.id}
    id={topic.id}
    title={topic.label}
    content={topic.content}
    link={topic.link}
    linkText={topic.linkText}
    imgSrc={topic.imgSrc}
    imgAlt={topic.imgAlt}
  />
))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};



export default MasteringTechnicalDrawings;

